/**
 * Which set of reads a screen is talking to — the seed's, or the backend's — and when they are stale.
 *
 * Mounted beside `V07CommandsProvider`, for the same reason and with the same shape: the screens ask
 * by name and never know which world answered, so the components the visual acceptance measures are
 * the components the product renders.
 *
 * The context carries a REVISION as well as the port. A read is identified by its question, and a
 * question whose answer has not changed must not be asked again on every render — but a successful
 * command changes the answers, and `useRead` has no way to know that from the question alone. So a
 * command that changed the world bumps this number, every read folds it into its identity, and the
 * screens refresh. Nothing else bumps it: a store write must never become a network request, or the
 * refresh loop this replaced comes back wearing a different hat.
 */
import { createContext, type ReactElement, type ReactNode, useContext } from 'react';
import type { V07Queries } from './queries';

interface ReadsContext {
  queries: V07Queries | null;
  /** Bumped once per successful world-changing command. */
  revision: number;
}

const QueriesContext = createContext<ReadsContext>({ queries: null, revision: 0 });

export function V07QueriesProvider({
  queries,
  revision = 0,
  children,
}: {
  queries: V07Queries;
  revision?: number;
  children: ReactNode;
}): ReactElement {
  /*
   * The value is rebuilt whenever either half changes, which is the point: a new revision has to
   * reach every `useRead` below, and there is nothing else in it to keep stable.
   */
  return <QueriesContext.Provider value={{ queries, revision }}>{children}</QueriesContext.Provider>;
}

/**
 * Throws rather than falling back to an empty page.
 *
 * A missing provider that answered «no records» would be indistinguishable from a database with none
 * in it — the exact confusion this whole layer exists to end.
 */
export function useV07Queries(): V07Queries {
  const { queries } = useContext(QueriesContext);
  if (queries === null) throw new Error('useV07Queries must be used inside V07QueriesProvider');
  return queries;
}

/** The current reads revision; 0 where no provider has been mounted. */
export function useReadsRevision(): number {
  return useContext(QueriesContext).revision;
}
